import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchComparison, fetchComparisons } from "../lib/api";
import Breadcrumbs from "../components/Breadcrumbs";
import ShareButtons from "../components/ShareButtons";
import { setCanonical, useNoindex } from "../lib/seo";
import ComparisonCard from "../components/ComparisonCard";

export default function ComparisonDetail() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  useNoindex(notFound || !!c?.noindex);

  useEffect(() => {
    setC(null);
    setNotFound(false);
    fetchComparison(slug)
      .then((data) => {
        setC(data);
        document.title = `${data.title} | SaaSRadarHub`;
        setCanonical(`/comparisons/${data.slug}`);
        fetchComparisons({ category: data.category }).then((list) =>
          setRelated(list.filter((x) => x.slug !== data.slug).slice(0, 2))
        );
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-24 text-center">
        <h1 className="font-playfair text-3xl mb-3">Comparison not found</h1>
        <Link to="/comparisons" className="text-indigo-800 underline">Back to comparisons</Link>
      </div>
    );
  }
  if (!c) return <div className="max-w-3xl mx-auto px-5 py-24 text-neutral-500">Loading…</div>;

  return (
    <article className="max-w-5xl mx-auto px-5 lg:px-8 py-14" data-testid="comparison-detail">
      <Breadcrumbs
        items={[
          { label: "Comparisons", to: "/comparisons" },
          { label: c.title },
        ]}
      />

      <header className="mb-10">
        <p className="eyebrow mb-4">{c.category} · Comparison</p>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight leading-[1.05] mb-5">
          {c.title}
        </h1>
        <p className="text-lg text-neutral-600 leading-relaxed max-w-3xl">{c.excerpt}</p>
        <p className="mt-6 text-xs text-neutral-500">Updated {c.updated_date} · {c.reading_time} min read</p>
        <div className="mt-6"><ShareButtons kind="comparisons" slug={c.slug} title={c.title} /></div>
      </header>

      {/* Quick verdict */}
      <section className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-7 mb-12">
        <p className="eyebrow mb-3">Quick verdict</p>
        <p className="text-lg text-neutral-800 leading-relaxed">{c.quick_verdict}</p>
      </section>

      {/* Best for */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14" data-testid="best-for">
        <div className="bg-surface border border-neutral-200 rounded-xl p-7">
          <p className="eyebrow mb-3">Best for</p>
          <h3 className="font-playfair text-2xl mb-3 text-neutral-900">{c.product_a}</h3>
          <p className="text-neutral-700 leading-relaxed">{c.best_for_a}</p>
        </div>
        <div className="bg-surface border border-neutral-200 rounded-xl p-7">
          <p className="eyebrow mb-3">Best for</p>
          <h3 className="font-playfair text-2xl mb-3 text-neutral-900">{c.product_b}</h3>
          <p className="text-neutral-700 leading-relaxed">{c.best_for_b}</p>
        </div>
      </section>

      {/* Feature table */}
      <section className="mb-14">
        <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Feature comparison</h2>
        <div className="overflow-x-auto border border-neutral-200 rounded-xl bg-surface">
          <table className="w-full min-w-[480px] text-left" data-testid="feature-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">Feature</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">{c.product_a}</th>
                <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">{c.product_b}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {c.feature_rows.map((r, i) => (
                <tr key={i}>
                  <td className="px-5 py-4 text-sm font-medium text-neutral-900">{r.feature}</td>
                  <td className="px-5 py-4 text-sm text-neutral-700">{r.a}</td>
                  <td className="px-5 py-4 text-sm text-neutral-700">{r.b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Strengths / Limitations */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
        <div className="bg-surface border border-neutral-200 rounded-xl p-7">
          <h3 className="font-playfair text-xl mb-4">{c.product_a} — Strengths</h3>
          <ul className="list-disc pl-5 space-y-2 text-neutral-700 text-sm leading-relaxed">
            {c.strengths_a.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <h4 className="eyebrow mt-6 mb-3">Limitations</h4>
          <ul className="list-disc pl-5 space-y-2 text-neutral-700 text-sm leading-relaxed">
            {c.limitations_a.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
        <div className="bg-surface border border-neutral-200 rounded-xl p-7">
          <h3 className="font-playfair text-xl mb-4">{c.product_b} — Strengths</h3>
          <ul className="list-disc pl-5 space-y-2 text-neutral-700 text-sm leading-relaxed">
            {c.strengths_b.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
          <h4 className="eyebrow mt-6 mb-3">Limitations</h4>
          <ul className="list-disc pl-5 space-y-2 text-neutral-700 text-sm leading-relaxed">
            {c.limitations_b.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      </section>

      {/* Use cases */}
      <section className="mb-14">
        <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-6">Common use cases</h2>
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {c.use_cases.map((u, i) => (
            <li key={i} className="bg-surface border border-neutral-200 rounded-xl p-5 text-neutral-800 text-sm leading-relaxed">
              <span className="font-playfair italic text-indigo-800 mr-2">0{i + 1}</span>
              {u}
            </li>
          ))}
        </ul>
      </section>

      {/* Pricing note */}
      <section className="mb-14 bg-neutral-50 border border-neutral-200 rounded-xl p-7">
        <p className="eyebrow mb-3">Pricing</p>
        <p className="text-neutral-700 leading-relaxed">{c.pricing_note}</p>
      </section>

      {/* Final recommendation */}
      <section className="bg-indigo-950 text-neutral-100 rounded-xl p-10 mb-14">
        <p className="eyebrow text-indigo-300 mb-4">Final recommendation</p>
        <p className="font-playfair text-2xl sm:text-3xl leading-tight">
          {c.final_recommendation}
        </p>
      </section>

      {related.length > 0 && (
        <section className="mt-20">
          <h2 className="font-playfair text-2xl sm:text-3xl text-neutral-900 mb-8">More comparisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {related.map((r) => <ComparisonCard key={r.id} comparison={r} />)}
          </div>
        </section>
      )}
    </article>
  );
}
